import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { formatMoney } from '../../../util/currency';
import { types as sdkTypes } from '../../../util/sdkLoader';

import { Modal, Button } from '../../../components';

import css from './BidUpdateSuccessModal.module.css';

const { Money } = sdkTypes;

/**
 * BidUpdateSuccessModal component displays a confirmation message
 * after a bid has been successfully updated.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} props.onManageDisableScrolling - Callback to manage scroll behavior
 * @param {Money} props.previousBidAmount - The previous bid amount
 * @param {Money} props.newBidAmount - The new updated bid amount
 * @param {string} props.marketplaceCurrency - The marketplace currency code
 *
 * @returns {React.Element} The BidUpdateSuccessModal component
 */
const BidUpdateSuccessModal = props => {
  const {
    isOpen,
    onClose,
    onManageDisableScrolling,
    previousBidAmount,
    newBidAmount,
    marketplaceCurrency,
  } = props;

  const intl = useIntl();

  // Ensure amounts are Money objects for formatting
  const previousMoney = previousBidAmount instanceof Money 
    ? previousBidAmount 
    : new Money(previousBidAmount, marketplaceCurrency);
  
  const newMoney = newBidAmount instanceof Money 
    ? newBidAmount 
    : new Money(newBidAmount, marketplaceCurrency);

  const formattedPreviousBid = formatMoney(intl, previousMoney);
  const formattedNewBid = formatMoney(intl, newMoney);

  return (
    <Modal
      id="BidUpdateSuccessModal"
      isOpen={isOpen}
      onClose={onClose}
      usePortal
      onManageDisableScrolling={onManageDisableScrolling}
      contentClassName={css.modalContent}
    >
      <div className={css.header}>
        <h2 className={css.title}>
          <FormattedMessage id="BidUpdateSuccessModal.title" />
        </h2>
        <p className={css.message}>
          <FormattedMessage id="BidUpdateSuccessModal.message" />
        </p>
      </div>

      <div className={css.bidDetails}>
        <div className={css.bidRow}>
          <span className={css.label}>
            <FormattedMessage id="BidUpdateSuccessModal.previousBid" />
          </span>
          <span className={css.amount}>{formattedPreviousBid}</span>
        </div>
        <div className={css.bidRow}>
          <span className={css.label}>
            <FormattedMessage id="BidUpdateSuccessModal.newBid" />
          </span>
          <span className={css.amountNew}>{formattedNewBid}</span>
        </div>
      </div>

      <div className={css.actions}>
        <Button
          type="button"
          onClick={onClose}
          className={css.okButton}
        >
          <FormattedMessage id="BidUpdateSuccessModal.ok" />
        </Button>
      </div>
    </Modal>
  );
};

export default BidUpdateSuccessModal;
